import { isNil } from 'lodash'
import { TbDownload } from 'react-icons/tb'
import { Link, useSearchParams } from 'react-router'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { WorkspaceButtonsDisplay } from '~/components/navigation/TableRowWorkspaceButtonsDisplay'
import { ChartDetailsActions } from '../charts/ChartDetailsActions'
import { SaveChartActions } from '../charts/SaveChartActions'
import { FormEditActions } from '../form-settings/FormEditActions'
import { FormMenu } from '../forms/FormMenu'
import { RecordDetailsMenu } from '../records/RecordDetailsMenu'
import { TableMenu } from '../shared/Table/TableMenu'
import { TableSettingsTitleBarActions } from '../table-settings/TableSettingsTitleBarActions'
import { AddNewFormButton } from '../workspaces/AddNewFormButton'
import { WorkspaceActions } from '../workspaces/WorkspaceActions'
import { ActivityFeedButton } from './ActivityFeedButton'
import { TitleBarProps } from './TitleBar'
import { UploadDataButton } from './UploadDataButton'

export function TitleBarActions({ sharedObject, tableRow, tableData, chart }: TitleBarProps) {
  const { isMobile } = useDeviceType()
  const { objectId, pageType, isPageWithActivityFeed, group, crumbs } = usePage()
  const { isAdmin, isInternal, permissionsManager } = useGlobalContext()
  const { t } = useTypedTranslation()
  const [searchParams] = useSearchParams()
  const workspaceId = searchParams.get('workspaceId')

  const getActionsComponent = () => {
    // For workspace pages, only show the workspace actions if the user can edit the workspace
    if (
      (pageType === 'workspace' || pageType === 'createWorkspace') &&
      objectId &&
      permissionsManager.canEditWorkspace(objectId)
    ) {
      return <WorkspaceActions workspaceId={objectId} />
    }

    // On table page: Upload Data when permitted, plus Settings for admin/internal or Exports for others
    if (pageType === 'table' && objectId) {
      const canUploadData = tableData != null && permissionsManager.canUploadDataToTable(tableData)
      const tablePageSecondaryAction =
        isAdmin || isInternal ? (
          <TableMenu tableId={objectId} />
        ) : (
          <Link
            className="flex cursor-pointer items-center justify-center gap-1.5 rounded p-2 hover:bg-gray-2"
            onClick={(e) => e.stopPropagation()}
            title={t('table.view_exports')}
            to={`/table/${objectId}/exports`}
          >
            <TbDownload size={12} />
            <span>{t('table.view_exports')}</span>
          </Link>
        )

      // Show the upload button only if they have permission to upload data
      // (can add row and have edit access for all columns)
      if (canUploadData) {
        return (
          <div className="flex items-center gap-2">
            <UploadDataButton table={tableData} />
            {tablePageSecondaryAction}
          </div>
        )
      }

      return tablePageSecondaryAction
    }

    // On the settings integrations page, the Add button is rendered in the header next to search
    if (group === 'settings' && crumbs.some((c) => c.label === 'breadcrumbs.connections')) {
      return null
    }

    // On the record view page, always show the Record Details Menu. The menu will only
    // display the options that are appropriate for the user based on their roles
    if (tableRow) {
      return <RecordDetailsMenu tableRow={tableRow} />
    }

    // On the forms home page, only display the button to create a new form if the user
    // is an admin or an internal Doss user
    if (!isMobile && pageType === 'formsHome' && (isAdmin || isInternal)) {
      return <AddNewFormButton />
    }

    // Only show the form menu for admin or internal users because it is a link to the form
    // settings page
    if (
      !isMobile &&
      group === 'forms' &&
      objectId &&
      pageType !== 'formSettings' &&
      (isAdmin || isInternal)
    ) {
      return <FormMenu formId={objectId} />
    }

    // On the form settings page, only display the form edit actions if the user is an admin or
    // internal Doss user. This should always be true since only admin and internal users should be
    // able to route to the table settings page but added this check just in case that changes.
    if (pageType === 'formSettings' && objectId && (isAdmin || isInternal)) {
      return <FormEditActions formId={objectId} />
    }

    // On the table settings page, only display the Upload Data button if the user can
    // upload data. This should always be true since only admin and internal users should be
    // able to route to the table settings page but added this check just in case that changes.
    // Copy Schema and Add Field render here too while the Fields tab is active.
    if (pageType === 'tableSettings' && tableData) {
      return (
        <div className="flex items-center gap-2">
          {permissionsManager.canUploadDataToTable(tableData) && (
            <UploadDataButton table={tableData} />
          )}
          <TableSettingsTitleBarActions table={tableData} />
        </div>
      )
    }

    // On the chart settings or create chart page, only display the button to save the chart
    // if the user has permission to configure the chart.
    if (pageType === 'chartSettings' && chart && permissionsManager.canConfigureChart(chart)) {
      return <SaveChartActions />
    }

    if (
      pageType === 'createChart' &&
      workspaceId &&
      permissionsManager.canEditWorkspace(workspaceId)
    ) {
      return <SaveChartActions />
    }

    // On the chart page, only display the button to route the user to the chart settings page
    // if the user has permission to configure the chart.
    if (pageType === 'chart' && chart && permissionsManager.canConfigureChart(chart)) {
      return <ChartDetailsActions chartId={objectId} chartType={chart.chartType} />
    }

    // On the pivot table page, only display the button to route the user to the pivot table
    // settings page if the user has permission to configure the pivot table.
    if (pageType === 'pivotTable' && !isNil(chart)) {
      if (permissionsManager.canConfigurePivotTables(chart.table.id)) {
        return <ChartDetailsActions chartId={objectId} chartType={chart.chartType} />
      }
    }

    // Create new pivot table page
    if (pageType === 'createPivotTable' || pageType === 'pivotTableSettings') {
      const pivotSourceTableId = !isNil(chart) ? chart.table.id : searchParams.get('tableId')

      // User needs to be able to configure pivot tables for the selected source table (tableId must be present)
      const canConfigurePivotTables =
        !isNil(pivotSourceTableId) && permissionsManager.canConfigurePivotTables(pivotSourceTableId)
      // If the creation entry point is a workspace (url contains workspaceId), then the user needs to have edit workspace permission
      const canEditWorkspaceIfProvided =
        !isNil(workspaceId) && permissionsManager.canEditWorkspace(workspaceId)

      if (canEditWorkspaceIfProvided && canConfigurePivotTables) {
        return <SaveChartActions />
      } else if (workspaceId === null && canConfigurePivotTables) {
        return <SaveChartActions />
      }
    }

    return null // Default, if no condition matches
  }

  const ActionsComponent = !sharedObject ? getActionsComponent() : false

  return (
    <div className="flex items-center justify-center gap-2">
      {!isMobile && tableRow && <WorkspaceButtonsDisplay tableRow={tableRow} />}
      {!isMobile && isPageWithActivityFeed && <ActivityFeedButton />}
      {ActionsComponent}
    </div>
  )
}
